import React, {useState, useRef, useEffect} from 'react';
import {Table, Button} from 'react-bootstrap';
import ImageIcon from '@mui/icons-material/Image';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { CancelRounded, Check } from '@mui/icons-material';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import useAxiosProtect from '../../../../../../hooks/useAxiosProtect';
import Form from 'react-bootstrap/Form';
import SensorButton from '../sensors_v2/SensorButton';
import SensorModal from '../sensors_v2/SensorModal';
import SensorCalibrationHistory from '../sensors_v2/SensorCalibrationHistory';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import SensorTelemetryIcons from '../sensors_v2/SensorTelemetryIcons';
import ManualReadingsModal from '../sensors_v2/ManualReadingsModal';
import useAuth from '../../../../../../hooks/useAuth';

const FlowieComponent = props => {
    const {auth} = useAuth();
    const floatRegex = /^(|\d*\.?\d+|\d+\.)$/;

    const axiosProtect = useAxiosProtect();
    var init = useRef(true);

    const fileInputRef = useRef(null);
    const selectedFlowieRef = useRef(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [siteID, setSiteID] = useState('');
    const [flowies, setFlowies] = useState('');
    const [modalState, setModalState] = useState(undefined);
    const [showManualReadings, setShowManualReadings] = useState(false);

    const [sensorToEdit, setSensorToEdit] = useState(null);
    const [sensorToDelete, setSensorToDelete] = useState(null);
    const [sensorToToggle, setSensorToToggle] = useState(null);
    const [sensorToImage, setSensorToImage] = useState(null);
    const [sensorForHistory, setSensorForHistory] = useState(null);

    // for date values in table:
    const timestampFormat = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "America/New_York",
        timeZoneName: "short",
    };
    
    const handleFileChange = async(e) => {
        const flowie = selectedFlowieRef.current;
        const file = e.target.files[0];
        let site_id = parseInt(window.location.href.split('/').slice(-1));
        if (file) {
        
        if (file.size > 5000000) {
            setSnackbarOpen(true);
        
            e.target.value = null;
            return;
        }
        const formData = new FormData()
        formData.append("file", file);
        formData.append("upload_preset", "stewwi")

        const res = await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY}/image/upload`, {
            method: "POST",
            body: formData
        })

        const uploaded = await res.json()

        if(!res.ok || !uploaded.url){
            throw new Error(uploaded.error?.message || 'Unable to upload image');
        }
        
        let payload = {
            file: uploaded.url,
            site_id,
            sensor_id: flowie.sensor_id || flowie.id,
        };

        axiosProtect.put('/v2s/v2-sensors/image', payload).then(res => {
            editSensor(res.data.sensor);
        }).catch(err => console.log(err));

        e.target.value = null;

        }
    };

    useEffect(() => {
        const controller = new AbortController();
        let site_id = parseInt(window.location.href.split('/').slice(-1))
        if (site_id) {
            // Set the site id
            setSiteID(site_id)
        }

        const getFlowies = async () => {
            try {
                const response = await axiosProtect.get(`/v2s/v2-sensors?site_id=${site_id}&utility=water`, {
                    signal:  controller.signal
                });

                let sensors = [];

                for(let i = 0;i<response.data.sensors.length;++i){
                    let sensor = {...response.data.sensors[i]};
                    
                    sensor.calibrationEditOn = false;
                    sensor.calibration_factor = sensor.calibration_factor.toString();
                    sensor.calibrationError = '';

                    sensors.push(sensor);
                }

                setFlowies(sensors);

                console.log(sensors)

            } catch(err) {
                console.log(err)
            }
        }

        if(init.current){
            init.current = false;
            getFlowies()
        }
    
    }, []);

    useEffect(() => {
        props.updateSensorCount('water', flowies.length);
    }, [flowies]);

    // Remove the row after deleting the row
    const removeRow = id => {

        let tempArray = [...flowies];

        for(let i = 0;i<tempArray.length;++i){
            if(tempArray[i].id === id){
                tempArray.splice(i, 1);
                break;
            }
        }

        setFlowies(tempArray)
    }

    const triggerFileUpload = (flowie) => {
        selectedFlowieRef.current = flowie;
        fileInputRef.current.click();
    }

    const updateCalibrationFactor = unit => {
        let unitCopy = {...unit};
        let newFactor = parseFloat(unitCopy.calibration_factor);
        let hasErrors = false;

        if(isNaN(newFactor)){
            hasErrors = true;
            unitCopy.calibrationError = 'Invalid input';

        }else if(newFactor <= 0){
            hasErrors = true;
            unitCopy.calibrationError = 'Must be > 0';
        }

        if(hasErrors){
            let sensors = [];
            for(let i = 0;i<flowies.length;++i){
                if(flowies[i].id !== unitCopy.id) sensors.push(flowies[i]);
                else sensors.push(unitCopy);
            }

            setFlowies([...sensors]);
            return;
        }

        axiosProtect.put('/v2s/v2-sensors/calibrate', {
            new_factor: parseFloat(unitCopy.calibration_factor),
            sensor_id: unitCopy.sensor_id || unitCopy.id,
            site_id: unitCopy.site_id
        }).then(response => {
            setFlowies(prevUnits =>
                prevUnits.map(item =>
                    item.id === unitCopy.id ? { 
                        ...item, 
                        calibration_factor: response.data.sensor.calibration_factor.toString(),
                        calibrationEditOn: false,
                        calibrationError: ''
                    } : item
                )
            );
        }).catch(error => {
            setFlowies(prevUnits =>
                prevUnits.map(item =>
                    item.id === unitCopy.id ? { 
                        ...item, 
                        calibrationEditOn: false,
                        calibrationError: error.response?.data?.error || 'Unable to update calibration factor'
                    } : item
                )
            );
        });
    }

    const handleClickToggleEdit = unit => {
        let unitCopy = {...unit};

        if(unitCopy.calibrationEditOn){
            unitCopy.calibrationEditOn = false;
            unitCopy.calibrationError = '';
            unitCopy.calibration_factor = unitCopy.calibration_factor.toString();
        }else{
            unitCopy.calibrationEditOn = true;
        }

        let sensors = [];
        for(let i = 0;i<flowies.length;++i){
            if(flowies[i].id !== unitCopy.id) sensors.push(flowies[i]);
            else sensors.push(unitCopy);
        }

        setFlowies([...sensors]);
    }

    const handleMouseDownEdit = event => {
        event.preventDefault();
    }

    const activateAddSensor = () => {
        setModalState('add');
    }

    const activateEditSensor = sensor => {
        setSensorToEdit(sensor);
        setModalState('edit');
    }

    const activateDeleteSensor = sensor => {
        setSensorToDelete(sensor);
        setModalState('delete');
    }

    const activateImageModal = sensor => {
        setSensorToImage(sensor);
        setModalState('image');
    }

    const requestToggleSensor = sensor => {
        if(sensor.active){
            setSensorToToggle(sensor);
            setModalState('toggle');
        }else{
            toggleSensor(sensor);
        }
    }
    
    const closeModal = () => {
        setSensorToDelete(null);
        setSensorToEdit(null);
        setSensorToToggle(null);
        setSensorToImage(null);
        setModalState(undefined);
    }

    const addSensor = sensor => {
        setFlowies(prev => 
            [...prev, sensor].sort((a, b) => 
                a.name.localeCompare(b.name)
            )
        );
    }

    const editSensor = sensor => {
        setFlowies(prev =>
            prev.map(existing => existing.id === sensor.id ? sensor : existing)
                .sort((a, b) => a.name.localeCompare(b.name))
        );
    }

    const deleteSensor = sensorID => {
        setFlowies(prev => prev.filter(sensor => sensor.id !== sensorID));
    }

    const toggleSensor = sensor => {
        axiosProtect.put('/v2s/v2-sensors/toggle-active', {
            sensor_id: sensor.sensor_id || sensor.id,
            site_id: sensor.site_id
        }).then(response => {
            setFlowies(prev => prev.map(existing => existing.id === response.data.sensor.id
                ? {...existing, active: response.data.sensor.active}
                : existing));
        }).catch(error => console.log(error));
    }

    const applyToggledSensor = sensor => {
        setFlowies(prev => prev.map(existing => existing.id === sensor.id
            ? {...existing, active: sensor.active}
            : existing));
    }

    return(
        <>
            {
                modalState !== undefined &&
                <SensorModal 
                    modalState={modalState} 
                    closeModal={closeModal} 
                    utility='water' 
                    sensorToDelete={sensorToDelete} 
                    sensorToEdit={sensorToEdit}
                    sensorToToggle={sensorToToggle}
                    sensorToImage={sensorToImage}
                    triggerFileUpload={triggerFileUpload}

                    addSensor={addSensor}
                    editSensor={editSensor}
                    deleteSensor={deleteSensor}
                    toggleSensor={applyToggledSensor}
                />
            }

            <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{display: 'none'}}/>

            <SensorCalibrationHistory selectedSensor={sensorForHistory} setSelectedSensor={setSensorForHistory}/>

            <ManualReadingsModal show={showManualReadings} closeModal={() => setShowManualReadings(false)} siteID={siteID} utility="water"/>

            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <SensorButton label='Add water sensor' utility='water' method='add' clickAction={activateAddSensor}/>
                {auth.role === process.env.REACT_APP_ES_ADMIN && <SensorButton label='Add manual readings' method='add' clickAction={() => setShowManualReadings(true)}/>}
            </div>

            <Table>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Supplier</th>
                        <th>Registered device name/DevEUI</th>
                        <th>Calibration factor</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {flowies ? flowies.map((flowie, i) => (
                        <tr key={flowies[i].flowie_name}>
                            <td>
                                {flowies[i].name}
                                <SensorTelemetryIcons battery={flowie.battery} rssi={flowie.rssi} snr={flowie.snr}/>
                            </td>
                            <td>
                                <Form.Check
                                    type="switch"
                                    id={`flowie-switch-${i}`}
                                    className='centrica-table-switch'
                                    onChange={() => requestToggleSensor(flowie)}
                                    label={flowie.active ? 'active' : 'disabled'}
                                    checked={flowie.active}
                                />
                            </td>
                            <td>{flowies[i].supplier_name}</td>
                            <td>
                                {
                                    flowies[i].supplier_name === 'Milesight' ? 
                                    flowies[i].deveui :
                                    flowies[i].supplier_name === 'Offline' ?
                                    flowies[i].device_type :
                                    flowies[i].device_name ? 
                                    flowies[i].device_name :
                                    'N/A'
                                }
                            </td>
                            {/* <td>{flowies[i].last_sync ? new Date(flowies[i].last_sync).toLocaleString("en-US", timestampFormat) : "N/A"}</td> */}
                            <td style={{display:'flex', flexDirection:'row'}}>
                                    <OutlinedInput
                                        id={`gas_calibration_field_${flowie.id}`}
                                        disabled={!flowie.calibrationEditOn}
                                        value={flowie.calibration_factor}
                                        error={flowie.calibrationError !== ''}
                                        helperText={flowie.calibrationError}
                                        onChange={e => {
                                            if(!floatRegex.test(e.target.value)) return;

                                            setFlowies(prevUnits =>
                                                prevUnits.map(item =>
                                                    item.id === flowie.id ? { ...item, calibration_factor: e.target.value } : item
                                                )
                                            );
                                        }}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                {flowie.calibrationEditOn ? 
                                                    <>
                                                    <IconButton onClick={() => updateCalibrationFactor(flowie)}>
                                                        <Check/>
                                                    </IconButton>
                                                    <IconButton edge="end" onClick={() => handleClickToggleEdit(flowie)}>
                                                        <CancelRounded/>
                                                    </IconButton>
                                                    </> 
                                                    :
                                                    <IconButton onClick={() => handleClickToggleEdit(flowie)} onMouseDown={handleMouseDownEdit} edge="end">
                                                        <EditIcon />
                                                    </IconButton>
                                                }
                                            </InputAdornment>
                                        }
                                    />
                            </td>
                            <td>
                                <IconButton onClick={() => setSensorForHistory(flowie)}>
                                    <ManageHistoryIcon/>
                                </IconButton>

                                {flowie.images != null ? (
                                    <IconButton onClick={() => activateImageModal(flowie)}>
                                        <ImageIcon style={{fill: "#0872B6"}}/>
                                    </IconButton>
                                ) : (
                                    <IconButton onClick={() => { selectedFlowieRef.current = flowie; fileInputRef.current.click(); }}>
                                        <AddPhotoAlternateIcon style={{fill: "#0872B6"}}/>
                                    </IconButton>
                                )}

                                <SensorButton 
                                    label='Delete water sensor' 
                                    utility='water' 
                                    method='delete'
                                    clickAction={() => activateDeleteSensor(flowie)}
                                />

                                <SensorButton 
                                    label='Edit water sensor' 
                                    utility='water' 
                                    method='edit'
                                    clickAction={() => activateEditSensor(flowie)}
                                />
                            </td>
                        </tr>
                        )
                    ) : <></>}
                </tbody>
            </Table>
        </>
    );
}


export default FlowieComponent;
